import React, { Component } from 'react';
import styled from 'styled-components';
import { changeLocation } from 'api/Location';
import { postRequest } from '../../api/ApiService';
import { fetchTracks, invalidateTracks } from '../../services/tracks-cache';
import { Loader } from 'react-loaders';
import Auth from '../../routes/Auth';

import Pagination from './Pagination';
import CopiedMessage from './CopiedMessage';
import Page404 from './Page404';

import WelcomeStep1 from './welcome/Step1';
import Round1Checklist from './welcome/Round1Checklist';
import RoundChecklist from './welcome/RoundChecklist';
import TeamsAndCompetitionsStep from './TeamsAndCompetitions';
import {
  CustomerPreferences,
  MachineOptions,
  DistributionCenters,
  ShareBuyback,
  ProductStrategy,
  NewMetricROE,
  NewMetricROA,
  NewMetricEPS,
  ConductResearch
} from './what-to-expect';
import NextStepsPage from './NextSteps';

import ConfettiExplosion from './ConfettiExplosion';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-self: stretch;

  .loader {
    display: inline-block;
    margin-top: 20px;
    margin-left: 20px;
  }

  .loader .loader-inner div {
    border-color: #36507e;
  }
`;

const LoaderWrapper = styled.div`
  background: #f5f6f7;
  padding: 40px;
  flex: 1;
`;

const ConfettiWrapper = styled.div`
  position: absolute;
  top: 59px;
  left: 475px;
  width: calc(100vw - 475px);
  height: calc(100vh - 139px);
  overflow: hidden;
`;

const ConfettiPosition = styled.div`
  position: absolute;
  top: 30%;
  left: 50%;
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: calc(30% + 59px);
  left: calc(50% + 130px);

  font-family: 'Mukta Vaani';
  font-style: normal;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  padding: 10px 20px;
  background: #FFBABA;
  border: 1px solid #D8000C;
  border-radius: 8px;
  z-index: 3000;
`;

/* Full-screen dim backdrop below the topbar. Topbar stays visible
   and clickable (logout works). No dismiss — student can only leave
   by logging out. */
const LockedBackdrop = styled.div`
  position: fixed;
  top: 59px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 27, 59, 0.55);
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const LockedCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 520px;
  padding: 32px 36px;
  text-align: center;
`;

const LockedMessage = styled.div`
  font-family: 'Mukta Vaani';
  font-style: normal;
  font-weight: 600;
  font-size: 22px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
  text-align: center;
  margin: 0;
`;

const LockedLogoutButton = styled.button`
  background: #2DA548;
  border: 1px solid #2DA548;
  border-radius: 4px;
  padding: 10px 28px;
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;
  margin-top: 24px;

  &:hover {
    background: #248339;
    border-color: #248339;
  }
`;

class OnboardingPage extends Component {

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      pages: [{
        title: 'Welcome',
        alias: 'welcome',
        steps: [
          { component: WelcomeStep1, alias: '' },
        ],
      }],
      currentPage: -2,
      currentStep: -2,
      mode: 'basic', // basic | disabled | validate | loading
      showMessage: false,
      validateCallback: null,
      history: [],
      showConfetti: false,
      isFetchData: false,
      showIncorrectMessage: false,
      navigationLoaded: false,
    };
  }

  componentDidMount() {
    // In the locked "no game yet" state we render a static message
    // only — skip the navigation/data fetch which would 404 or return
    // empty since the student has no game.
    if (this.props.noGameYet) return;
    this.loadNavigation(() => {
      this.getCurrentPage();
    });
  }

  setFetchData = (isFetchData) => {
    this.setState({
      isFetchData,
    });
  }

  showConfetti = () => {
    this.setState({
      showConfetti: true,
    }, () => {
      setTimeout(() => {
        this.setState({
          showConfetti: false,
        })
      }, 5000);
    });
  }

  showIncorrectMessage = () => {
    this.setState({
      showIncorrectMessage: true,
    }, () => {
      setTimeout(() => {
        this.setState({
          showIncorrectMessage: false,
        })
      }, 5000);
    });
  }

  loadNavigation = (callback) => {
    const { location, roundInUrl, currentRound } = this.props;
    // Resolve `round` to a NUMBER before comparing with `=== 1`. The
    // sources arrive in mixed types:
    //   - location.query.round → string (React Router 3 raw query)
    //   - roundInUrl prop      → string (App.js reads it raw, not
    //                            parsed — see App.js state init)
    //   - currentRound prop    → number
    // Coerce first, then fall back through the chain so any entry
    // point (deep link, sidebar click, redirect) resolves the same
    // way. Skipping the coercion previously caused Round 1 to land
    // on the rounds-2+ checklist because `"1" === 1` is false.
    let round = null;
    if (location && location.query && location.query.round) {
      const parsed = parseInt(location.query.round, 10);
      if (!isNaN(parsed)) round = parsed;
    }
    if (round == null && roundInUrl != null) {
      const parsed = parseInt(roundInUrl, 10);
      if (!isNaN(parsed)) round = parsed;
    }
    if (round == null) {
      round = currentRound;
    }

    {
        // Round 1 lands students on Round1Checklist (specific to
        // first round). Rounds 2+ use the shared RoundChecklist
        // which adapts its substeps based on round + active targets.
        // The legacy WelcomeStep1 file is kept in the repo but is
        // no longer mounted.
        const stepsWelcome = round === 1
          ? [
            { component: Round1Checklist, alias: 'intro' },
          ]
          : [{ component: RoundChecklist, alias: '' }];

        let tempPages = [
          {
            title: 'Welcome',
            alias: 'welcome',
            steps: stepsWelcome,
          }
        ];
        if (round === 1) {
          tempPages.push({
            title: 'Teams & Competitions',
            alias: 'teams-and-competitions',
            steps: [
              { component: TeamsAndCompetitionsStep, alias: '' },
            ],
          });
        }

        if (round > 3) {
          tempPages.push({
            title: 'What to Expect',
            alias: '',
            hasChilds: true,
            steps: [
              { alias: '' },
            ],
          }, {
            title: 'Customer Preferences',
            alias: 'customer-preferences',
            subpage: true,
            steps: [
              { component: CustomerPreferences, alias: '' },
            ],
          });
          if (round === 2) {
            tempPages.push({
              title: 'Product Strategy',
              alias: 'product-strategy',
              subpage: true,
              steps: [
                { component: ProductStrategy, alias: '' },
              ],
            }, {
              title: 'Machine Options',
              alias: 'machine-options',
              subpage: true,
              steps: [
                { component: MachineOptions, alias: '' },
              ],
            }, {
              title: 'Distribution Centers',
              alias: 'distribution-centers',
              subpage: true,
              steps: [
                { component: DistributionCenters, alias: '' },
              ],
            });
          }

          if (round === 3) {
            tempPages.push({
              title: 'Share Buyback',
              alias: 'share-buyback',
              subpage: true,
              steps: [
                { component: ShareBuyback, alias: '' },
              ],
            }, {
              title: 'New Metric: ROE',
              alias: 'new-metric-roe',
              subpage: true,
              steps: [
                { component: NewMetricROE, alias: '' },
              ],
            }, {
              title: 'New Metric: ROA',
              alias: 'new-metric-roa',
              subpage: true,
              steps: [
                { component: NewMetricROA, alias: '' },
              ],
            }, {
              title: 'Conduct Research',
              alias: 'conduct-research',
              subpage: true,
              steps: [
                { component: ConductResearch, alias: '' },
              ],
            });
          }

          if (round === 4) {
            tempPages.push({
              title: 'New Metric: EPS',
              alias: 'new-metric-eps',
              subpage: true,
              steps: [
                { component: NewMetricEPS, alias: '' },
              ],
            }, {
              title: 'Conduct Research',
              alias: 'conduct-research',
              subpage: true,
              steps: [
                { component: ConductResearch, alias: '' },
              ],
            });
          }
        }

        tempPages.push({
          title: 'Next Steps',
          alias: 'next-steps',
          steps: [
            { component: NextStepsPage, alias: '' },
          ]
        });

        this.setState({
          pages: tempPages,
          navigationLoaded: true,
        }, () => {
          if (callback) {
            callback();
          }
        });
    }
  }

  componentDidUpdate() {
    const { navigationLoaded } = this.state;

    if (navigationLoaded === true) {
      this.getCurrentPage();
    }
  }

  getLastNotFinishedPage = (trackedPages, pages) => {
    let prevPage = 0;
    let prevStep = 0;
    for (let pageNum = 0; pageNum < pages.length; pageNum++) {
      for (let stepNum = 0; stepNum < pages[pageNum].steps.length; stepNum++) {
        const pageAlias = pages[pageNum].alias;
        const stepAlias = pages[pageNum].steps[stepNum].alias;

        if (pageAlias === '' && stepAlias === '') {
          continue;
        }

        const foundPage = trackedPages.find(i => i[0] === pageAlias && i[1] === stepAlias);
        if (!foundPage) {
          return {
            page: prevPage,
            step: prevStep,
          };
        }

        prevPage = pageNum;
        prevStep = stepNum;
      }
    }

    return {
      page: prevPage,
      step: prevStep,
    };
  }

  get activeRound() {
    const { currentRound, roundInUrl } = this.props;

    return roundInUrl || currentRound;
  }

  getCurrentPage() {
    const { location } = this.props;
    const { pages } = this.state;
    const currentRound = this.activeRound;

    const pathes = location.pathname.substring(1).split('/');

    let currentPage = 0;
    let currentStep = 0;
    let doRedirect = true;
    if (pathes.length > 1) {
      currentPage = -1;
      currentStep = -1;
      doRedirect = false;

      const category = pages.findIndex(i => i.alias === pathes[1]);
      if (category > -1) {
        if (pathes.length === 3) {
          const step = pages[category].steps.findIndex(i => i.alias === pathes[2]);
          if (step > -1) {
            currentPage = category;
            currentStep = step;
          }
        } else {
          currentPage = category;
          currentStep = 0;
        }
      }

      if (
        currentPage !== this.state.currentPage ||
        currentStep !== this.state.currentStep
      ) {
        this.setState({
          loading: true,
          currentPage,
          currentStep,
        });

        const user_id = Number(Auth.getUserId());
        const round_number = Number(currentRound);
        const game_id = Number(Auth.getGameId());
        const course_id = Number(Auth.getCourseId());
        fetchTracks({ gameId: game_id, courseId: course_id, userId: user_id, roundNumber: round_number })
          .then(res => {
            let history = res.body.results.filter(i => (
              i.page.startsWith(`${window.location.protocol}//${window.location.host}/onboarding/`)
            )).map(i => i.page.replace(`${window.location.protocol}//${window.location.host}/onboarding/`, ''));

            this.setState({
              loading: false,
              history,
            }, () => {
              if (doRedirect) {
                const page = pages[currentPage].alias;
                const step = pages[currentPage].steps[currentStep].alias;

                changeLocation(
                  `/onboarding/${page}${step !== '' ? '/' + step : ''}?round=${currentRound}`
                );
              }
            });
          })
          .catch(err => { });
      }
    } else {
      if (this.state.loading !== true) {
        this.setState({
          loading: true,
        });

        const user_id = Number(Auth.getUserId());
        const round_number = Number(currentRound);
        const game_id = Number(Auth.getGameId());
        const course_id = Number(Auth.getCourseId());
        fetchTracks({ gameId: game_id, courseId: course_id, userId: user_id, roundNumber: round_number })
          .then(res => {
            let history = res.body.results.filter(i => (
              i.page.startsWith(`${window.location.protocol}//${window.location.host}/onboarding/`)
            )).map(i => i.page.replace(`${window.location.protocol}//${window.location.host}/onboarding/`, ''));

            // The old behavior on a bare /onboarding URL was to use
            // tracks history to find the "last not finished page",
            // which landed students on Next Steps once they'd been
            // through the flow. With the consolidated checklist we
            // always want the leftbar Welcome link to open the
            // checklist, so we leave currentPage/currentStep at 0/0
            // (= Welcome's first step) regardless of tracks state.

            if (doRedirect) {
              const page = pages[currentPage].alias;
              const step = pages[currentPage].steps[currentStep].alias;

              if (currentPage === 0 && currentStep === 0 && !history.includes(`${page}${step !== '' ? '/' + step : ''}`)) {
                postRequest(`${process.env.REACT_APP_API_URL}v2/engagement/tracks/`)
                  .send({
                    page: `${window.location.protocol}//${window.location.host}/onboarding/${page}${step !== '' ? '/' + step : ''}`,
                    user: Auth.getUserId(),
                    course: Auth.getCourseId(),
                    game: Auth.getGameId(),
                    round_number: currentRound,
                  })
                  .then((data) => {
                    // New tracks entry — drop the cache so the next
                    // read picks it up.
                    invalidateTracks();
                    history = [
                      ...history,
                      `${page}${step !== '' ? '/' + step : ''}`,
                    ];

                    this.setState({
                      history,
                    });
                  });
              }
              changeLocation(
                `/onboarding/${page}${step !== '' ? '/' + step : ''}?round=${currentRound}`
              );
            }

            this.setState({
              loading: false,
              currentPage,
              currentStep,
              history,
            });
          })
          .catch(err => {
            console.log(err);
          });
      }
    }
  }

  trackPage = (url, page, step) => {
    const currentRound = this.activeRound;

    const user_id = Number(Auth.getUserId());
    const round_number = Number(currentRound);
    const game_id = Number(Auth.getGameId());
    const course_id = Number(Auth.getCourseId());
    fetchTracks({ gameId: game_id, courseId: course_id, userId: user_id, roundNumber: round_number })
      .then(res => {
        const current_page = `${window.location.protocol}//${window.location.host}${url}`;

        const history = res.body.results.filter(i => (
          i.page.startsWith(`${window.location.protocol}//${window.location.host}/onboarding/`)
        )).map(i => i.page.replace(`${window.location.protocol}//${window.location.host}/onboarding/`, ''));
        const trackedPage = res.body.results.find(i => (
          i.page === current_page
        ));

        changeLocation(`${url}?round=${currentRound}`);

        if (!trackedPage) {
          postRequest(`${process.env.REACT_APP_API_URL}v2/engagement/tracks/`)
            .send({
              page: current_page,
              user: user_id,
              course: course_id,
              game: game_id,
              round_number: round_number,
            })
            .then((data) => {
              invalidateTracks();
              this.setState({
                history: [
                  ...history,
                  `${page}${step !== '' ? '/' + step : ''}`,
                ],
              });
            });
        }
      })
      .catch(err => { });
  }

  goNext = e => {
    e.preventDefault();

    const { pages, currentPage, currentStep } = this.state;

    let nextPage = currentPage;
    let nextStep = currentStep + 1;
    if (nextStep > pages[nextPage].steps.length - 1) {
      nextPage = currentPage + 1;
      nextStep = 0;

      if (nextPage > pages.length - 1) {
        return;
      }
    }

    if (pages[nextPage].hasChilds) {
      nextPage += 1;
    }

    const page = pages[nextPage].alias;
    const step = pages[nextPage].steps[nextStep].alias;

    this.trackPage(
      `/onboarding/${page}${step !== '' ? '/' + step : ''}`,
      page,
      step,
    );

    this.setState({
      currentPage: nextPage,
      currentStep: nextStep,
      showIncorrectMessage: false,
    });
  }

  goPrev = e => {
    e.preventDefault();

    const { pages, currentPage, currentStep } = this.state;

    let prevPage = currentPage;
    let prevStep = currentStep - 1;
    if (prevStep < 0) {
      prevPage = currentPage - 1;
      if (prevPage < 0) {
        return;
      }

      prevStep = pages[prevPage].steps.length - 1;
    }

    if (pages[prevPage].hasChilds) {
      prevPage -= 1;
    }

    const page = pages[prevPage].alias;
    const step = pages[prevPage].steps[prevStep].alias;

    changeLocation(
      `/onboarding/${page}${step !== '' ? '/' + step : ''}?round=${this.activeRound}`
    );

    this.setState({
      currentPage: prevPage,
      currentStep: prevStep,
    });
  }

  onValidate = e => {
    e.preventDefault();

    const { validateCallback } = this.state;

    if (validateCallback) {
      validateCallback();
    }
  }

  get isFirstStep() {
    const { currentPage, currentStep } = this.state;

    return currentPage === 0 && currentStep === 0;
  }

  get isLastStep() {
    const { pages, currentPage, currentStep } = this.state;

    return (
      currentPage === pages.length - 1 &&
      currentStep === pages[pages.length - 1].steps.length - 1
    );
  }

  setMode = (mode, validateCallback = null) => {
    if (['basic', 'disabled', 'validate', 'loading'].includes(mode) && mode !== this.state.mode) {
      this.setState({
        mode,
        validateCallback,
      });
    }
  }

  showMessage = () => {
    this.setState({
      showMessage: true,
    }, () => {
      setTimeout(() => {
        this.setState({
          showMessage: false,
        });
      }, 5000);
    });
  }

  render() {
    // Locked "Waiting on Instructor" state: student is authenticated
    // but no game exists for the picked course yet. Renders as a
    // popup card centered over a fixed dim backdrop that covers
    // everything below the topbar. Topbar stays visible and
    // clickable so logout is reachable. No dismiss affordance.
    if (this.props.noGameYet) {
      // Skeleton Welcome page renders behind the dim backdrop so
      // students see the shape of the app they'll enter once the
      // instructor sets up the exercise. Real Welcome components
      // (Round1Checklist etc.) require game/round data that isn't
      // available yet — we render static placeholder blocks
      // instead. Popup + Log Out sit on top.
      return (
        <Wrapper>
          <Content>
            <div style={{
              flex: 1,
              minHeight: 'calc(100vh - 60px)',
              background: '#f5f6f7',
              padding: 40,
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: 12,
                padding: '32px 40px',
                maxWidth: 900,
              }}>
                <div style={{
                  fontFamily: 'Mukta Vaani',
                  fontWeight: 700,
                  fontSize: 28,
                  lineHeight: '36px',
                  color: 'rgba(0, 27, 59, 0.94)',
                  marginBottom: 24,
                }}>
                  Welcome
                </div>
                <div style={{ background: '#f0f2f4', height: 20, width: '55%', borderRadius: 4, marginBottom: 24 }} />
                <div style={{ background: '#f0f2f4', height: 280, borderRadius: 12, marginBottom: 28 }} />
                <div style={{ background: '#f0f2f4', height: 16, width: '82%', borderRadius: 4, marginBottom: 12 }} />
                <div style={{ background: '#f0f2f4', height: 16, width: '76%', borderRadius: 4, marginBottom: 12 }} />
                <div style={{ background: '#f0f2f4', height: 16, width: '68%', borderRadius: 4 }} />
              </div>
            </div>
          </Content>
          <LockedBackdrop>
            <LockedCard>
              <LockedMessage>
                {this.props.studentLockReason === 'round_not_started'
                  ? 'Round Has Not Started Yet.'
                  : 'Waiting on Instructor to set up exercise.'}
              </LockedMessage>
              <LockedLogoutButton
                type="button"
                onClick={() => {
                  if (Auth.isUserAuthenticated()) {
                    Auth.deauthenticateUser();
                  }
                  changeLocation("/login");
                }}
              >
                Log Out
              </LockedLogoutButton>
            </LockedCard>
          </LockedBackdrop>
        </Wrapper>
      );
    }

    const {
      currentPage,
      currentStep,
      pages,
      mode,
      showMessage,
      loading,
      history,
      showConfetti,
      isFetchData,
      showIncorrectMessage,
    } = this.state;
    const { rounds, roundInUrl } = this.props;
    const currentRound = this.activeRound;
    const ContentPage = loading
      ? null
      : currentPage > -1 && currentStep > -1
        ? pages[currentPage].steps[currentStep].component
        : Page404;
    const data = loading
      ? null
      : currentPage > -1 && currentStep > -1
        ? pages[currentPage].steps[currentStep].data || null
        : null;
    const pageAlias = loading
      ? null
      : currentPage > -1
        ? pages[currentPage].alias
        : null;

    return (
      <Wrapper>
        {/* Step-nav sidebar removed — onboarding is consolidating to
            a single checklist page per round. The Navigation component
            file remains in the repo in case we need it again, but it
            is no longer rendered. */}
        {showConfetti && (
          <ConfettiWrapper>
            <ConfettiPosition>
              <ConfettiExplosion
                force={0.6}
                duration={3000}
                particleCount={100}
                height={1000}
                width={1000}
              />
            </ConfettiPosition>
          </ConfettiWrapper>
        )}
        {showIncorrectMessage && <ErrorMessage>This is incorrect!</ErrorMessage>}
        <Content>
          {ContentPage &&
            <ContentPage
              setMode={this.setMode}
              showMessage={this.showMessage}
              currentRound={currentRound}
              rounds={rounds}
              data={data}
              stage="welcome"
              showConfetti={this.showConfetti}
              setFetchData={this.setFetchData}
              isFetchData={isFetchData}
              showIncorrectMessage={this.showIncorrectMessage}
              roundInUrl={roundInUrl}
            />}
          {!ContentPage && (
            <LoaderWrapper>
              <Loader type="ball-scale-ripple" active />
            </LoaderWrapper>
          )}
          {showMessage && <CopiedMessage />}
          {currentPage > -1 && currentStep > -1 &&
            <Pagination
              currentStep={currentStep + 1}
              countOfSteps={pages[currentPage].steps.length}
              goNext={this.goNext}
              goPrev={this.goPrev}
              onValidate={this.onValidate}
              isFirstStep={this.isFirstStep}
              isLastStep={this.isLastStep}
              isNextDisabled={mode === 'disabled'}
              isValidationMode={mode === 'validate'}
              isLoadingMode={mode === 'loading'}
              isTriviaPage={pageAlias === 'trivia'}
            />}
        </Content>
      </Wrapper>
    );
  }

}

export default OnboardingPage;



// WEBPACK FOOTER //
// ./src/components/onboarding/OnboardingPage.js